import React, { useState, useEffect } from 'react';
import { X, Calendar, Target, CheckCircle, Loader } from 'lucide-react';
import { Document, Matter, Project, MatterDeadline, db } from '../../lib/db';
import { toast } from 'sonner';
import { TemplateModal } from '../templates/TemplateModal';
import { useNavigate } from 'react-router-dom';

interface ProcessingModalProps {
  document: Document;
  dbUserId: string;
  onClose: () => void;
  onProcessed: () => void;
  isDeadlineOnly?: boolean;
}

interface ProcessingAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  action: () => void;
}

export function ProcessingModal({ document, dbUserId, onClose, onProcessed, isDeadlineOnly = false }: ProcessingModalProps) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [processingNotes, setProcessingNotes] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  // Deadline form state
  const [showDeadlineForm, setShowDeadlineForm] = useState(false);
  const [deadlineTitle, setDeadlineTitle] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineReminder, setDeadlineReminder] = useState('');
  

  useEffect(() => {
    loadData();
    // If deadline only mode, show the deadline form immediately
    if (isDeadlineOnly) {
      setShowDeadlineForm(true);
    }
  }, []);

  const loadData = async () => {
    try {
      const [mattersList, projectsList] = await Promise.all([
        db.matters.findByUserId(dbUserId),
        db.projects.findByUserId(dbUserId)
      ]);
      setMatters(mattersList);
      setProjects(projectsList);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleCreateDeadline = async () => {
    if (!selectedMatterId || !deadlineTitle || !deadlineDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const deadline = await db.matterDeadlines.create({
        matter_id: selectedMatterId,
        user_id: dbUserId,
        title: deadlineTitle,
        description: `Created from document: ${document.filename}`,
        due_date: new Date(deadlineDate),
        reminder_date: deadlineReminder ? new Date(deadlineReminder) : undefined,
        is_completed: false
      });
      
      // Record the action
      await db.documentActions.create({
        document_id: document.id,
        user_id: dbUserId,
        action_type: 'create_deadline',
        action_data: { deadline_id: deadline.id, matter_id: selectedMatterId }
      });
      
      toast.success('Deadline created successfully');
      setShowDeadlineForm(false);
      resetDeadlineForm();
    } catch (error) {
      console.error('Error creating deadline:', error);
      toast.error('Failed to create deadline');
    }
  };
  
  
  const handleCompleteProcessing = async () => {
    setProcessing(true);
    
    try {
      // Update document status to processed
      await db.documents.update(document.id, dbUserId, {
        status: 'processed',
        processed_at: new Date(),
        processing_notes: processingNotes
      });
      
      toast.success('Document processing completed');
      onProcessed();
    } catch (error) {
      console.error('Error completing processing:', error);
      toast.error('Failed to complete processing');
    } finally {
      setProcessing(false);
    }
  };
  
  const resetDeadlineForm = () => {
    setDeadlineTitle('');
    setDeadlineDate('');
    setDeadlineReminder('');
    setSelectedMatterId(null);
  };
  

  const actions: ProcessingAction[] = [
    {
      id: 'deadline',
      label: 'Create Deadline',
      icon: <Calendar className="w-5 h-5" />,
      color: 'text-red-600',
      action: () => setShowDeadlineForm(true)
    },
    {
      id: 'deliverable',
      label: 'Create Deliverable',
      icon: <Target className="w-5 h-5" />,
      color: 'text-green-600',
      action: () => setShowTemplateModal(true)
    }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Process Document</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">{document.filename}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Filed on {new Date(document.upload_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            {!showDeadlineForm && (
              <>
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    What actions does this document trigger?
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {actions.map((action) => (
                      <button
                        key={action.id}
                        onClick={action.action}
                        className={`flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors ${
                          selectedAction === action.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                        }`}
                      >
                        <span className={action.color}>{action.icon}</span>
                        <span className="text-sm font-medium text-gray-900">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Processing Notes (Optional)
                  </label>
                  <textarea
                    value={processingNotes}
                    onChange={(e) => setProcessingNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Add any notes about processing this document..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteProcessing}
                    disabled={processing}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {processing ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin inline mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        Complete Processing
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
            
            {/* Deadline Form */}
            {showDeadlineForm && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Create Deadline</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Matter
                    </label>
                    <select
                      value={selectedMatterId || ''}
                      onChange={(e) => setSelectedMatterId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select a matter...</option>
                      {matters.filter(m => !m.is_archived).map(matter => (
                        <option key={matter.id} value={matter.id}>
                          {matter.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deadline Title
                    </label>
                    <input
                      type="text"
                      value={deadlineTitle}
                      onChange={(e) => setDeadlineTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter deadline title..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={deadlineDate}
                        onChange={(e) => setDeadlineDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reminder Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={deadlineReminder}
                        onChange={(e) => setDeadlineReminder(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowDeadlineForm(false);
                        resetDeadlineForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateDeadline}
                      disabled={!selectedMatterId || !deadlineTitle || !deadlineDate}
                      className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Create Deadline
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Template Modal for Creating Deliverables */}
      {showTemplateModal && (
        <TemplateModal
          isOpen={true}
          onClose={() => setShowTemplateModal(false)}
          onSuccess={async () => {
            // Record the action
            await db.documentActions.create({
              document_id: document.id,
              user_id: dbUserId,
              action_type: 'create_deliverable',
              action_data: { /* deliverable details would go here */ }
            });
            
            setShowTemplateModal(false);
            toast.success('Deliverable created successfully');
          }}
        />
      )}
    </div>
  );
}